import React, { useState, useEffect } from 'react';
import { SearchResults } from './SearchResults';
import ProductCardSkeleton from '../Skeleton/ProductCardSkeleton';
import '../../css/components/Skeleton/Skeleton.css';
import '../../css/components/ProductCategories/ProductCategories.css';
import { useSearch } from '../../hooks/useFrontApi';
import { attachInsigniasToProducts } from '../../utils/insignias';

const SKELETON_CARDS_COUNT = 12;
const PER_PAGE = 24;

const SearchComponent = ({ searchQuery = '' }) => {
  const [page, setPage] = useState(1);

  // Insignias (badges): la API de búsqueda no las resuelve. Las traemos una
  // sola vez y las mapeamos sobre los resultados (igual que hace el build).
  const [insignias, setInsignias] = useState([]);
  useEffect(() => {
    let active = true;
    const fetchAllInsignias = async () => {
      try {
        let p = 1, last = 1, all = [];
        do {
          const res = await fetch(`${process.env.BASE_URL}/insignias?page=${p}`);
          if (!res.ok) break;
          const json = await res.json();
          all = all.concat(json.data || []);
          last = json.last_page || 1;
          p += 1;
        } while (p <= last);
        if (active) setInsignias(all);
      } catch (e) { /* silencioso */ }
    };
    fetchAllInsignias();
    return () => { active = false; };
  }, []);

  const trimmedQuery = String(searchQuery).trim();
  const hasQuery = trimmedQuery.length >= 2;

  const { data: searchData, loading: loadingLive, error: errorLive } = useSearch(
    hasQuery ? searchQuery : '',
    page,
    PER_PAGE
  );

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const results = searchData?.data ?? [];
  const currentPage = searchData?.current_page ?? 1;
  const lastPage = searchData?.last_page ?? 1;
  const total = searchData?.total ?? 0;
  // La tolerancia a typos ahora la resuelve el servidor: cuando no hay match
  // exacto devuelve resultados similares y marca fuzzy=true para avisar al usuario.
  const usedFallback = searchData?.fuzzy ?? false;

  // Adjuntar insignias antes de pasarlas a SearchResults (no rompe el flujo
  // si insignias aún no llegaron — devuelve los productos con insignias=[]).
  const normalizedProducts = attachInsigniasToProducts(
    results.map((p) => ({
      ...p,
      attributes: p.attributes || [],
    })),
    insignias
  );

  // Query vacío o sin contenido
  if (trimmedQuery === '') {
    return (
      <div style={{ margin: '15px 0', padding: '1rem' }}>
        <p>Escribe algo para buscar productos.</p>
      </div>
    );
  }

  // Query con menos de 2 caracteres
  if (trimmedQuery.length < 2) {
    return (
      <div style={{ margin: '15px 0', padding: '1rem' }}>
        <p>Escribe al menos 2 caracteres para buscar.</p>
      </div>
    );
  }

  if (loadingLive) {
    return (
      <div className="layoutSearch" style={{ margin: '15px 0' }}>
        <div className="Content">
          <div className="Content__productCategories">
            <div className="container">
              <div className="Content__productCategories--title" style={{ marginBottom: 24, display: 'block' }}>
                <p>Buscando &quot;{searchQuery}&quot;...</p>
              </div>
              <div className="Content__productCategories--grid">
                <div className="Content__productCategories--cards">
                  {Array.from({ length: SKELETON_CARDS_COUNT }).map((_, i) => (
                    <ProductCardSkeleton key={`skeleton-${i}`} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (errorLive) {
    return (
      <div style={{ margin: '15px 0', padding: '1rem' }}>
        <p>No se pudieron cargar los resultados. Intenta de nuevo.</p>
      </div>
    );
  }

  if (normalizedProducts.length === 0) {
    return (
      <div style={{ margin: '15px 0', padding: '1rem' }}>
        <p>No se encontraron resultados para &quot;{searchQuery}&quot;.</p>
      </div>
    );
  }

  return (
    <div style={{ margin: 0 }}>
      {usedFallback && (
        <div style={{ margin: 0, padding: '0.5rem 1rem', backgroundColor: '#fff3cd', borderRadius: '4px', fontSize: '14px', color: '#856404' }}>
          No se encontraron resultados exactos. Mostrando resultados similares.
        </div>
      )}
      <SearchResults
        results={normalizedProducts}
        searchQuery={searchQuery}
        pagination={{
          currentPage,
          lastPage,
          total,
          perPage: PER_PAGE,
          onPageChange: setPage,
        }}
        totalResults={total}
      />
    </div>
  );
};

export default SearchComponent;
